<template>
  <div :class="{ 'custom-in-dialog': isFromListing }">
    <div class="custom-center">
      <z-message v-model="message" class="alert alert-warning mt-2" />
      <p v-if="!isGotInspection">
        To view a vehicle's ADAS systems, components, locations, <span class="custom-important">POTENTIAL</span> calibrations, OE based
        calibration requirements and more, please enter or paste a VIN in the field below.
      </p>
      <p class="mb-1">{{ isGotInspection ? 'VIN:' : 'Enter (or paste) vehicle VIN # here:' }}</p>
      <form @submit.prevent="processVin(null)">
        <z-input v-model="vin" name="vin" placeholder="vehicle VIN number" :maxlength="20" :validator="v$.vin" :disabled="isGotInspection" />
      </form>
    </div>
    <!-- Same intermediate step as ADAS Map: set RO, insurance and notes before viewing. -->
    <div v-if="isGotInspection" class="custom-center">
      <p>{{ vehicleInfo }}</p>
      <z-input v-model="ronumber" name="ronumber" label="RO Number" />
      <z-select v-model="insurance" name="insurance" label="Insurance" :options="insuranceCompanies" />
      <z-textarea v-model="notes" name="notes" label="Notes" rows="4" />
    </div>
    <div v-if="isVehicles" class="custom-center">
      <p>Found {{ vehicles.length }} vehicles. Please choose the correct version.</p>
      <z-table v-model="vehicles" class="table table-bordered table-striped" :is-single-row-detail="false" :columns="vehicleColumns">
        <template #tbody="props">
          <td>{{ props.row.year }}</td>
          <td>{{ props.row.make }}</td>
          <td>{{ props.row.model }}</td>
          <td>{{ props.row.trimName }}</td>
          <td>{{ props.row.versionName }}</td>
          <td>
            <a href="#" @click.prevent="processVin(props.row.manufacturerCode)">choose</a>
          </td>
        </template>
        <template #empty> Nothing found! </template>
      </z-table>
    </div>
    <div v-else-if="isGotInspection" class="custom-center text-end mt-3">
      <button type="button" class="btn btn-secondary me-1" :disabled="isLoading" @click="cancel">Clear</button>
      <button type="button" class="btn btn-secondary me-1" :disabled="isLoading" @click="viewInspection">View</button>
      <button type="button" class="btn btn-primary" :disabled="isLoading" @click="updateInspection">
        Update
        <i v-show="isLoading" class="fas fa-spinner fa-spin"></i>
      </button>
    </div>
    <div v-else class="custom-center text-end">
      <button type="button" class="btn btn-secondary me-1" @click="cancel">Cancel</button>
      <button type="button" class="btn btn-primary" :disabled="isLoading" @click="processVin(null)">
        Generate Overview
        <i v-show="isLoading" class="fas fa-spinner fa-spin"></i>
      </button>
    </div>
    <div v-if="!isGotInspection" class="custom-center mt-5">
      <p>
        <span class="custom-important">IMPORTANT!</span> Generating an ADAS Systems and Calibration Overview DOES NOT result in a list of Actual
        Systems, Calibration requirements or other repairs. To receive a more accurate result please ensure you “scrub” your estimate or Repair
        Order.
      </p>
    </div>
    <div v-if="!isGotInspection && !isFromListing" class="custom-center mt-5">
      <p>
        To view an ADAS Inspection, click <router-link to="/inspections"><b>here</b></router-link>.
      </p>
    </div>
  </div>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, maxLength, helpers } from '@vuelidate/validators'
import { api } from '@/plugins/api'
import { authStore } from '@/stores/auth'
import { inspectionStore } from '@/stores/inspection'
import { lookupStore } from '@/stores/lookup'
import globals from '@/plugins/globals'
import ZInput from '@/components/utilities/elements/Input.vue'
import ZSelect from '@/components/utilities/elements/Select.vue'
import ZTextarea from '@/components/utilities/elements/TextArea.vue'
import ZMessage from '@/components/utilities/elements/Message.vue'
import ZTable from '@/components/utilities/elements/Table.vue'
// #668: mirrors ADAS Map's form-main.vue. Used by the Systems Overview page and the Repairs list Add dialog;
// the parent opens the inspection dialog from the 'open' event.
export default {
  name: 'OverviewForm',
  components: {
    ZInput,
    ZSelect,
    ZTextarea,
    ZMessage,
    ZTable
  },
  props: {
    isFromListing: {
      type: Boolean,
      default: false
    }
  },
  emits: ['open', 'created'],
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      vin: '',
      vehicles: [],
      adasInspection: null,
      ronumber: '',
      insurance: null,
      notes: '',
      message: '',
      isLoading: false,
      vehicleColumns: [
        { field: 'year', label: 'Year' },
        { field: 'make', label: 'Make' },
        { field: 'model', label: 'Model' },
        { field: 'trimName', label: 'Trim' },
        { field: 'versionName', label: 'Version' },
        { field: '', label: '' }
      ]
    }
  },
  validations() {
    // Same rule as ADAS Map (required, 17-20 chars), not Pro's stricter check-digit test.
    return {
      vin: {
        required: helpers.withMessage('VIN is required', required),
        minLength: helpers.withMessage('VIN must be 17 to 20 characters', minLength(17)),
        maxLength: helpers.withMessage('VIN must be 17 to 20 characters', maxLength(20))
      }
    }
  },
  async mounted() {
    await this.getLookups({
      keys: ['Status', 'Reason', 'SystemType', 'CCCItemType', 'Insurance'],
      firm: 1,
      refresh: false
    })
  },
  computed: {
    ...mapState(authStore, ['profile']),
    ...mapState(lookupStore, ['lookups']),
    isVehicles() {
      return this.vehicles.length > 0
    },
    isGotInspection() {
      return this.adasInspection !== null
    },
    vehicleInfo() {
      const v = this.adasInspection?.vehicle
      return v ? [v.year, v.make, v.model, v.bodystyle].filter((x) => x).join(' ') : ''
    },
    insuranceCompanies() {
      return this.lookups['Insurance'] ? globals.sortArray(this.lookups['Insurance'], 'name') : []
    }
  },
  methods: {
    ...mapActions(inspectionStore, ['getInspection', 'setLoading']),
    ...mapActions(lookupStore, ['getLookups']),
    cancel() {
      this.vin = ''
      this.adasInspection = null
      this.ronumber = ''
      this.insurance = null
      this.notes = ''
      this.vehicles = []
      this.message = ''
      this.v$.$reset()
    },
    async processVin(code) {
      this.vehicles = []
      this.message = ''
      if (!(await this.v$.$validate())) return
      this.isLoading = true
      try {
        let response = await api.post('adas/createAdas', {
          firm: this.profile.firm,
          adas: null,
          ronumber: null,
          vin: this.vin.trim(),
          insurance: null,
          notes: null,
          createdBy: this.profile.contact,
          manufacturerCode: code ?? null
        })
        console.log('createAdas response', response)
        if (typeof response === 'string') response = response ? JSON.parse(response) : {}
        if (response.error !== undefined) {
          this.message = response.error
        } else if (response.vehicleChoices !== undefined) {
          this.vehicles = response.vehicleChoices
        } else if (response.adasInspection !== undefined) {
          // Raw record (not the Pro view), since the save needs its status id
          const adasInspection = parseInt(response.adasInspection)
          let record = await api.get(`adas/adasInspection?adasInspection=${adasInspection}`)
          if (typeof record === 'string') record = record ? JSON.parse(record) : null
          if (!record || record.error) {
            this.message = record?.error || `Inspection #${adasInspection} was created but could not be loaded.`
            return
          }
          this.adasInspection = record
          this.ronumber = record.ronumber ?? ''
          this.insurance = record.insurance ?? null
          this.notes = record.notes ?? ''
          this.$emit('created', adasInspection)
        } else {
          this.message = 'No overview was returned for this VIN.'
        }
      } catch (e) {
        console.error('Generate Overview failed', e)
        this.message = typeof e === 'string' && e ? e : 'Unable to generate the overview. Please try again.'
        this.setLoading({ name: 'inspection', value: false })
      } finally {
        this.isLoading = false
      }
    },
    // Mirrors ADAS Map's Update: save RO/insurance/notes, then open the inspection.
    async updateInspection() {
      const record = this.adasInspection
      this.message = ''
      this.isLoading = true
      try {
        let response = await api.post('adas/saveAdasInspection', {
          adas: record.adas,
          adasInspection: record.adasInspection,
          firm: record.firm,
          insurance: this.insurance === null || this.insurance === '' ? null : parseInt(this.insurance),
          ronumber: this.ronumber,
          inspector: this.profile.contact,
          notes: this.notes,
          status: record.status
        })
        if (typeof response === 'string') response = response ? JSON.parse(response) : {}
        if (response?.error) {
          this.message = response.error
          return
        }
      } catch (e) {
        console.error('Update inspection failed', e)
        this.message = typeof e === 'string' && e ? e : 'Unable to update the inspection. Please try again.'
        return
      } finally {
        this.isLoading = false
      }
      await this.viewInspection()
    },
    async viewInspection() {
      const adasInspection = parseInt(this.adasInspection.adasInspection)
      const inspection = await this.getInspection(adasInspection)
      if (!inspection || inspection.error) {
        this.message = inspection?.error || `Inspection #${adasInspection} could not be loaded.`
        return
      }
      this.$emit('open', { adasInspection, adas: parseInt(this.adasInspection.adas), vin: this.vin.trim(), tab: 'ADAS Map' })
      // reset so the page behind the inspection dialog is ready for the next VIN
      this.cancel()
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-center {
  width: 50%;
  max-width: 625px;
  margin: 0 auto;
}
.custom-in-dialog .custom-center {
  width: 100%;
  max-width: none;
}
.custom-important {
  color: red;
  font-weight: 600;
}
</style>
